import { useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableRow, Button, IconButton, TableHead, Box, Stack, TableContainer } from '@mui/material';
import { FormatIndentIncrease, Close } from '@mui/icons-material';
import QueryRules from './QueryRules';
import {
	canGroupSelectedRules,
	getAllGroupPaths,
	groupSelectedRules,
	outerGroupSelectedRules,
} from './utilities';
import { useTheme } from '@mui/material/styles';

const AdvancedBuilder = ({ 
	fields, 
	query, 
	setQuery, 
	handleFieldChange, 
	fieldValueOptionsCallback, 
	valueField, 
	onCheckboxChange, 
	isReapplied, 
	selectedCheckbox, 
	onGroupPathsChange, 
	selGroupPaths, 
	handleClear,
	isClearDisabled,
	handleOperatorChange,
	parentHeight,
	isProjectDetails }: any) => {
	const theme = useTheme();
	const [selectedPaths, setSelectedPaths] = useState<any>([]);
	const groupPaths = useRef<number[][]>([]);
	const defaultRuleRef = useRef(false);
	const handleSetQuery = (newQuery: any, isAddRow: boolean = false) => {
		let grpPath = getAllGroupPaths(newQuery.rules).slice(1);
		groupPaths.current = grpPath;
		setQuery(newQuery);
		setSelectedPaths([]);
		onCheckboxChange([]);
		onGroupPathsChange?.(grpPath);
	};

	const handleCheckboxChange = (path: any) => {
		setSelectedPaths((prevPaths: any) => {
			let updatedPaths;
			if (prevPaths.some((p: any) => JSON.stringify(p) === JSON.stringify(path))) {
				updatedPaths = prevPaths.filter((p: any) => JSON.stringify(p) !== JSON.stringify(path));
			} else {
				updatedPaths = [...prevPaths, path];
			}

			onCheckboxChange(updatedPaths);
			return updatedPaths;
		});
	};

	const handleGroupAction = () => {
		const canGroupRes = canGroupSelectedRules(query, selectedPaths);
		if (canGroupRes.canGroup) {
			const updatedQuery =
				canGroupRes.groupType === 'outer'
					? outerGroupSelectedRules(query, selectedPaths)
					: groupSelectedRules(query, selectedPaths);
			handleSetQuery(updatedQuery);
		}
	};

		  useEffect(() => {
			const newInitialValue = [
				{
					field: '',
					operator: '',
					value: ''
				},
			];
		if (!defaultRuleRef.current && (!query.rules || query.rules.length === 0)) {
			if (!query.rules || query.rules.length === 0) {
				const newQuery = {
					rules: [...newInitialValue] 
				};
				handleSetQuery(newQuery);
				defaultRuleRef.current = false;
			} 
		}
	}, [fields, valueField]);

	useEffect(() => {
	  	setSelectedPaths(selectedCheckbox ?? []);
	  	groupPaths.current = selGroupPaths ?? [];
	}, [isReapplied])
	
	return (
		<TableContainer
			sx={{
				height: '100%',
				...(!parentHeight || parentHeight <= 300
					? { maxHeight: '300px' }
					: { maxHeight: 'none' }),
				flex: 1,
				overflow: 'auto', mb:'5px',
				'& .MuiTableCell-root, & .MuiTableRow-root': {
					color: 'text.subtle'
				},
				'& .MuiCheckbox-root': {
					color: 'text.subtle',
					'&.Mui-checked': {
						color: 'text.subtle'
					}
				}
			}}
		>
			<Table sx={{ borderCollapse: 'separate', borderSpacing: isProjectDetails ? '16px 6px' : 6 }} stickyHeader>
				{/* Headers */}
				<TableHead>
					<TableRow sx={{ color: 'dark.dark' }}>
						<TableCell sx={{ width: '130px !important', background: '#ffffff !important' }}>
							<Stack direction="row" justifyContent="flex-start" width={130}>
								<Button
									variant="text"
									sx={{ minWidth: 60, color: '#A9ADCB !important', cursor:'pointer', 
										'&:hover': {
											color: 'red !important',
											'& .MuiSvgIcon-root': {
												color: 'red !important',
											}, 
										},
									}}
									startIcon={<Close sx={{ color: '#A9ADCB' }} />}
									disabled={isClearDisabled}
									onClick={handleClear}
								>
									CLEAR ALL
								</Button>
							</Stack>
						</TableCell>
						{!isProjectDetails && (
							<TableCell width={50} sx={{background: '#ffffff !important'}}>
								<IconButton
									disabled={!canGroupSelectedRules(query, selectedPaths).canGroup}
									sx={{ color: 'text.subtle' }}
									onClick={() => handleGroupAction()}
								>
									<FormatIndentIncrease
										sx={{
											color: `${theme.palette.table.checkBox} !important`,
											fill: `${theme.palette.table.checkBox} !important`,

											'& path': {
												fill: `${theme.palette.table.checkBox} !important`,
											},
										}}
									/>
								</IconButton>
							</TableCell>
						)}
						{/* group tree branch style */}
						{!!groupPaths.current.length && (
							<TableCell component="th" variant="head" sx={{background: '#ffffff !important'}}>
								<Box />
							</TableCell>
						)}
						<TableCell sx={{fontWeight:'500', color:'#000000 !important', background: '#ffffff !important'}} width={120} component="th" variant="head">
							And/Or
						</TableCell>
						<TableCell sx={{fontWeight:'500', color:'#000000 !important', background: '#ffffff !important'}} width={400} component="th" variant="head">
							Field
						</TableCell>
						<TableCell sx={{fontWeight:'500', color:'#000000 !important', background: '#ffffff !important'}} width={240} component="th" variant="head">
							Operator
						</TableCell>
						<TableCell sx={{fontWeight:'500', color:'#000000 !important', background: '#ffffff !important'}} component="th" variant="head">
							Value 
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					<QueryRules
						rules={query.rules}
						fields={fields}
						query={query}
						setQuery={handleSetQuery}
						onCheckboxChange={handleCheckboxChange}
						checkedRules={selectedPaths}
						groupPaths={groupPaths}
						handleFieldChange={handleFieldChange}
						fieldValueOptionsCallback={fieldValueOptionsCallback}
						handleOperatorChange = {handleOperatorChange}
						isProjectDetails={isProjectDetails}
					/>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default AdvancedBuilder;
